// ---------------------------------------------------------------------------
// CreateEventForm — inline form for creating a new event binding.
// ---------------------------------------------------------------------------

import { useState } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { useEntityIndex } from '../hooks/use-entity-index'
import { toast } from '@/store/toast-store'
import { cn } from '@/lib/utils'

const EVENT_TYPES = ['tap', 'change', 'submit', 'focus', 'blur', 'longPress', 'init'] as const

interface Props {
  onClose: () => void
  defaultBlockName?: string
}

export function CreateEventForm({ onClose, defaultBlockName }: Props) {
  const project = useStudioStore((s) => s.currentProject)
  const addEventBinding = useStudioStore((s) => s.addEventBinding)
  const index = useEntityIndex()
  const blocks = project ? Object.keys(project.blocks) : []

  const [componentId, setComponentId] = useState('')
  const [eventType, setEventType] = useState<string>('tap')
  const [flow, setFlow] = useState('')
  const [blockName, setBlockName] = useState(defaultBlockName ?? blocks[0] ?? '')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!componentId.trim()) {
      toast.error('Component ID is required')
      return
    }
    if (!blockName) {
      toast.error('Select a block')
      return
    }

    const eventKey = `${componentId.trim()}:${eventType}`
    addEventBinding(blockName, eventKey, {
      flow: flow.trim() || undefined,
    })
    toast.success(`Event "${eventKey}" bound`)
    onClose()
  }

  return (
    <form onSubmit={handleSubmit} className="px-3 py-2 space-y-2 border-b border-border bg-accent/30">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">New Event Binding</span>
        <button type="button" onClick={onClose} className="text-[10px] text-muted-foreground hover:text-foreground">Cancel</button>
      </div>

      {blocks.length > 1 && (
        <select
          value={blockName}
          onChange={(e) => setBlockName(e.target.value)}
          className="w-full h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {blocks.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      )}

      <input
        autoFocus
        value={componentId}
        onChange={(e) => setComponentId(e.target.value)}
        placeholder="Component ID (e.g. submit-btn)"
        className="w-full h-7 rounded border border-border bg-background px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
      />

      <select
        value={eventType}
        onChange={(e) => setEventType(e.target.value)}
        className="w-full h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>

      <select
        value={flow}
        onChange={(e) => setFlow(e.target.value)}
        className="w-full h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      >
        <option value="">-- Select Flow --</option>
        {index.flows.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
      </select>

      <button
        type="submit"
        className={cn(
          'w-full h-7 rounded text-xs font-medium transition-colors',
          componentId.trim()
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground cursor-not-allowed',
        )}
        disabled={!componentId.trim()}
      >
        Create Event Binding
      </button>
    </form>
  )
}
